import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceSftp,
  UserImportDataSourceStatus,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import {
  Button,
  DialogActions,
  DialogContent,
  TextField,
  Typography
} from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DataGridVertical } from '../../../../../../components/DataGrid';
import { DateFormatPicker } from '../../../../../../components/DateFormatPicker';
import { InfoBox } from '../../../../../../components/InfoBox';
import * as ENVS from '../../../../../../env.json';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc, updateDoc } from '../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import { CustomImportMultiMappingForm2 } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.SFTP;

const createUserAndImportSource = async (
  orgId: string,
  sourceName: string,
  createdBy: string
) => {
  const { username, password, directory } = await callFirebaseFunction<{
    username: string;
    password: string;
    directory: string;
  }>('sftp-createUser', {
    orgId
  });

  const doc: Doc<UserImportDataSourceSftp> = {
    id: shortid(),
    collection: FS.userImportDataSources,
    data: {
      orgId,
      type: UserImportSourceType.SFTP,
      status: UserImportDataSourceStatus.OK,
      detailedStatus: '',
      name: sourceName,
      username,
      password,
      dateFormats: [], // TODO set a reasonable default
      folder: directory as any,
      createdBy,
      createdAt: now(),
      lastUpdate: null,
      customMappings: []
    }
  };

  await setDoc(doc);
  return doc;
};

const ConfigureImportSource = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceSftp>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);
  const [isDirty, setIsDirty] = useState(false);

  const [state, setState] = useState<
    Pick<
      UserImportDataSourceSftp,
      'customMappings' | 'inferredOtherUsers' | 'dateFormats'
    >
  >({
    customMappings: d.data.customMappings || [],
    inferredOtherUsers: d.data.inferredOtherUsers || [],
    dateFormats: d.data.dateFormats || []
  });

  return (
    <>
      <DialogContent>
        <FlexContainerVertical spacing={2}>
          <InfoBox color="blue" fullWidth>
            {t('Use the credentials below to send us your CSV files.')}
          </InfoBox>
          <SftpCredentialsInfoBox d={d} />
          <DateFormatPicker
            value={state.dateFormats}
            onChange={(formats) => {
              setIsDirty(true);
              setState((x) => ({ ...x, dateFormats: formats }));
            }}
            subLabel={t(
              'Select the date formats that your CSV file may contain. If none selected, the organization default will be used.'
            )}
          />
          <CustomImportMultiMappingForm2
            additionalCompositeFields={['$FULL_NAME$']}
            enableSave={isDirty}
            fieldTerm={t('column')}
            integrationName={t('your CSV file')}
            userFields={userFields}
            value={{
              main: state.customMappings,
              others: state.inferredOtherUsers?.map((v) => v.customMappings)
            }}
            onChange={(nextVal) => {
              setIsDirty(true);
              setState((x) => ({
                ...x,
                customMappings: nextVal.main,
                inferredOtherUsers:
                  nextVal.others?.map((o) => ({
                    customMappings: o
                  })) || []
              }));
            }}
          />
        </FlexContainerVertical>
      </DialogContent>
      <DialogActions>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          pending={t('Saving')}
          disabled={!isDirty}
          onClick={async () => {
            await updateDoc(d, () => ({
              customMappings: state.customMappings,
              inferredOtherUsers: state.inferredOtherUsers,
              dateFormats: state.dateFormats,
              lastUpdate: now()
            }));
            onClose();
          }}
        >
          {t('Save')}
        </ButtonWithPromise>
      </DialogActions>
    </>
  );
};

export const SftpCredentialsInfoBox = ({
  d
}: {
  d: Doc<UserImportDataSourceSftp>;
}) => {
  return (
    <InfoBox color="yellow" fullWidth>
      <DataGridVertical
        ds={[
          { label: 'Username', d: d.data.username },
          { label: 'Password', d: d.data.password },
          { label: 'Hostname', d: ENVS.sftp.serverLocation },
          { label: 'Upload Folder', d: d.data.folder }
        ]}
      />
    </InfoBox>
  );
};

export const SftpSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const { id: userId } = useCurrentUser();

  const [name, setName] = useState('');

  const [
    importSource,
    setImportSource
  ] = useState<Doc<UserImportDataSourceSftp> | null>(null);

  if (importSource) {
    return (
      <ConfigureImportSource
        d={importSource}
        onClose={onClose}
        customFields={customFields}
      />
    );
  }

  return (
    <DialogContent>
      <FlexContainerVertical spacing={2}>
        <Img maxHeight={128} src={META.logoUrl} />
        <CustomInputLabel
          label={t('Source Name')}
          subLabel={t('To be shown on the Dado Import Sources page')}
          fullWidth
        >
          <TextField
            placeholder={t('e.g. Employee Data from HRIS')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
          />
        </CustomInputLabel>

        <InfoBox color="blue" fullWidth>
          <FlexContainerVertical spacing={1} fullWidth>
            <Typography variant="body1">
              {t(
                'Dado can import data from CSV files sent via SFTP. Many systems can be set up to export selected data in CSV format via SFTP.'
              )}
            </Typography>
            <Typography variant="body1">
              {t(
                'At the next step, you will be provided with the credentials needed to start sending these files. Please refer to the help documentation for the system exporting data, for how to use these credentials.'
              )}
            </Typography>
          </FlexContainerVertical>
        </InfoBox>

        <FlexContainer justifyContent="flex-end" fullWidth>
          <Button>{t('Cancel')}</Button>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending={t('Creating Folder')}
            disabled={!name}
            onClick={async () => {
              const nextImportSource = await createUserAndImportSource(
                orgId,
                name,
                userId
              );
              setImportSource(nextImportSource);
            }}
          >
            {t('Setup SFTP')}
          </ButtonWithPromise>
        </FlexContainer>
      </FlexContainerVertical>
    </DialogContent>
  );
};

export const ImportSourceRowSftp = ({
  d
}: {
  d: Doc<UserImportDataSourceSftp>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
